import { useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import SortIcon from '@mui/icons-material/Sort';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClearIcon from '@mui/icons-material/Clear';
import { Calendar } from 'primereact/calendar';
import DOMPurify from 'dompurify';
import TextField from '@mui/material/TextField';
import Switch from 'react-switch';
import Checkbox from '@mui/material/Checkbox';
import { Chip, Badge } from '@mui/material';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import UserFileClobViewService from '@Helpers/URLHelpers/KzqFileService/UserFileClobViewService';
import ErrorHandler from '@Root/ErrorHandler';
import fileDownload from 'js-file-download';
import '@Partials/_ComStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './FileFilter.scss'

export default function FileFilter(props) {
    const [filters, setFilters] = useState(props.filters);
    const [searchQuery, setSearchQuery] = useState('');
    const [showTypeOptions, setShowTypeOptions] = useState(false);
    const [anchorTypeOptionsMenu, setAnchorTypeOptionsMenu] = useState(null);
    const [showFileStoryTypeOptions, setShowFileStoreTypeOptions] = useState(false);
    const [anchorFileStoreTypeOptionsMenu, setAnchorFileStoreTypeOptionsMenu] = useState(null);
    const [showCreateDateOptions, setShowCreateDateOptions] = useState(false);
    const [anchorCreateDateOptionsMenu, setAnchorCreateDateOptionsMenu] = useState(null);
    const [showSortByOptions, setShowSortByOptions] = useState(false);
    const [showDownloadOptions, setShowDownloadOptions] = useState(false);
    const [anchorDownloadOptionsMenu, setAnchorDownloadOptionsMenu] = useState(null);
    const [createDateRange, setCreateDateRange] = useState(null);
    const [anchorSortByOptionsMenu, setAnchorSortByOptionsMenu] = useState(null);
    const [showCreatedByListOption, setShowCreatedByListOption] = useState(false);
    const [anchorCreatedByListOptionMenu, setAnchorCreatedByListOptionMenu] = useState(null);
    const [createDateOption, setCreateDateOption] = useState(false);
    const [sortByOption, setSortByOption] = useState('createTimeStamp');
    const [maintainFolder, setMaintainFolder] = useState(false);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [refresh, setRefresh] = useState(false);
    const userFileClobViewService = new UserFileClobViewService();
    const errorHandler = new ErrorHandler();
    const className = 'file-filters';

    const handleDownload = async () => {
        props.setCursor('wait');
        handleDownloadOptionsPopup();
        try {
            let tempFilter = filters;
            tempFilter.maintainFolderStructure = maintainFolder;
            let response = await userFileClobViewService.exportFilesToZip(tempFilter);
            if(response.status < 400) {
                response = response.body;
                const currentDate = new Date();
                const day = currentDate.getDate().toString().padStart(2, '0');
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const month = monthNames[currentDate.getMonth()];
                const year = currentDate.getFullYear();
                const hours = currentDate.getHours() % 12 || 12;
                const minutes = currentDate.getMinutes().toString().padStart(2, '0');
                const ampm = currentDate.getHours() >= 12 ? 'PM' : 'AM';
                const filename = `Files_${day}_${month}_${year}_${hours}_${minutes}${ampm}.zip`;
                fileDownload(dataURLtoFile(response, filename), filename);
                setSnackBarOpen(true);
                setSnackBarMessage('Download started successfully');
                setSnackBarType('success');
            } else {
                setSnackBarOpen(true);
                setSnackBarMessage('Download failed');
                setSnackBarType('error');
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        props.setCursor('auto');
    }

    const dataURLtoFile = (encodedString, filename) => {
        try {
            const bstr = atob(encodedString);
            const n = bstr.length;
            const u8arr = new Uint8Array(n);
            for (let i = 0; i < n; i++) {
                u8arr[i] = bstr.charCodeAt(i);
            }
            return new File([u8arr], filename);
        }catch (err) {
            errorHandler.handleError(err);
        }
    };

    const handleSearchQueryChange = (e) => {
        let sanitizedQuery = DOMPurify.sanitize(e.target.value);
        setSearchQuery(sanitizedQuery);
        let filtersObj = props.filters;
        filtersObj.filter.searchQuery = sanitizedQuery;
        props.onFilterChange(filtersObj);
    }

    const handleFileStoreTypeOptionsPopup = (event) => {
        if(!showFileStoryTypeOptions){
            if(!!event) {
                setAnchorFileStoreTypeOptionsMenu(event.currentTarget);
                setShowFileStoreTypeOptions(true);
            }
        } else {
            setAnchorFileStoreTypeOptionsMenu(null);
            setShowFileStoreTypeOptions(false);
        }
    }

    const handleTypeOptionsPopup = (event) => {
        if(!showTypeOptions){
            if(!!event) {
                setAnchorTypeOptionsMenu(event.currentTarget);
                setShowTypeOptions(true);
            }
        } else {
            setAnchorTypeOptionsMenu(null);
            setShowTypeOptions(false);
        }
    }

    const handleCreateDateOptionsPopup = (event) => {
        if(!showCreateDateOptions){
            if(!!event) {
                setAnchorCreateDateOptionsMenu(event.currentTarget);
                setShowCreateDateOptions(true);
            }
        } else {
            setAnchorCreateDateOptionsMenu(null);
            setShowCreateDateOptions(false);
        }
    }

    const handleSortByOptionsPopup = (event) => {
        if(!showSortByOptions){
            if(!!event) {
                setAnchorSortByOptionsMenu(event.currentTarget);
                setShowSortByOptions(true);
            }
        } else {
            setAnchorSortByOptionsMenu(null);
            setShowSortByOptions(false);
        }
    }

    const handleDownloadOptionsPopup = (event) => {
        if(!showDownloadOptions){
            if(!!event) {
                setAnchorDownloadOptionsMenu(event.currentTarget);
                setShowDownloadOptions(true);
            }
        } else {
            setAnchorDownloadOptionsMenu(null);
            setShowDownloadOptions(false);
        }
    }

    const handleCreatedByOptionPopup = (event) => {
        if(!showCreatedByListOption){
            if(!!event) {
                setAnchorCreatedByListOptionMenu(event.currentTarget);
                setShowCreatedByListOption(true);
            }
        } else {
            setAnchorCreatedByListOptionMenu(null);
            setShowCreatedByListOption(false);
        }
    }

    const toggleCreatedByCheckBox = (assigneeUserIdChange) => {
        let filtersObj = filters;
        if(!filtersObj?.filter?.userIdList) {
            filtersObj.filter.userIdList = [];
        }
        if(filtersObj?.filter?.userIdList?.some((assigneeUserId) => assigneeUserId === assigneeUserIdChange)) {
            let newAssigneeUserIdList = filtersObj?.filter?.userIdList?.filter((assigneeUserId) => assigneeUserId !== assigneeUserIdChange);
            filtersObj.filter.userIdList = newAssigneeUserIdList;
        } else {
            filtersObj.filter.userIdList = [...filtersObj.filter.userIdList, assigneeUserIdChange];
        }
        props.onFilterChange(filtersObj);
        setFilters(filtersObj);
        setRefresh(!refresh);
    }

    const toggleTypeCheckbox = (type) => {
        let filtersObj = filters;
        if(!filtersObj?.filter?.tableTypeList) {
            filtersObj.filter.tableTypeList = [];
        }
        const types = ['EPIC', 'STORY', 'STAGE', 'MILESTONE', 'TASK', 'TICKET'];
        if(types.includes(type)) {
            if(filtersObj?.filter?.tableTypeList?.some((item) => item === type)) {
                let newTypeList = filtersObj?.filter?.tableTypeList?.filter((item) => item !== type);
                filtersObj.filter.tableTypeList = newTypeList;
            } else {
                filtersObj.filter.tableTypeList = [...filtersObj.filter.tableTypeList, type];
            }
        }
        props.onFilterChange(filtersObj);
        setFilters(filtersObj);
        setRefresh(!refresh);
    }

    const toggleFileStoreType = (storeType) => {
        let filtersObj = filters;
        if(!filtersObj?.filter?.fileStoreTypeList) {
            filtersObj.filter.fileStoreTypeList = [];
        }
        if(storeType === 'comment' || storeType === 'attachment') {
            if(filtersObj?.filter?.fileStoreTypeList?.some((item) => item === storeType)) {
                let newFileStoreType = filtersObj?.filter?.fileStoreTypeList?.filter((item) => item !== storeType);
                filtersObj.filter.fileStoreTypeList = newFileStoreType;
            } else {
                filtersObj.filter.fileStoreTypeList = [...filtersObj.filter.fileStoreTypeList, storeType];
            }
        }
        props.onFilterChange(filtersObj);
        setFilters(filtersObj);
        setRefresh(!refresh);
    }

    const handleSortByChange = (e) => {
        let sortByOptionObj = e.target.value;
        let filtersObj = filters;
        filtersObj.filter.sortBy = sortByOptionObj;
        props.onFilterChange(filtersObj);
        setSortByOption(sortByOptionObj);
        setFilters(filtersObj);
        setRefresh(!refresh);
    }

    const toggleSortAsc = (e) => {
        let filtersObj = filters;
        filtersObj.filter.sortAsc = !filtersObj.filter.sortAsc;
        props.onFilterChange(filtersObj);
        setFilters(filtersObj);
        setRefresh(!refresh);
    }

    const toggleMaintainFolder = (e) => {
        setMaintainFolder(!maintainFolder);
    }

    const clearFilters = (e) => {
        setSearchQuery('')
        props.onFilterChange(props.defaultFilters)
        setSortByOption('createTimeStamp');
        setFilters(props.defaultFilters);
        setRefresh(!refresh);
        props.clearFilters();
    }

    const handleSearchOnKeyPress = (e) => {
        if(e.keyCode === 13) {
            props.handleSearch(filters);
        }
    }

    const handleCreateDateOptionChange = (e) => {
        let createDateOptionObj = e.target.value;
        setCreateDateOption(createDateOptionObj);
        let filtersObj = filters;
        if(createDateOptionObj === 'thisWeek') {
            filtersObj.filter.createTimeStampFrom = new Date((new Date()).getTime() - ((((new Date()).getDay() + 6 ) % 7 ) * 1000 * 60 * 60 * 24)).getTime();
            filtersObj.filter.createTimeStampTo = (new Date()).getTime();
            let rangeObj = [new Date((new Date()).getTime() - ((((new Date()).getDay() + 6 ) % 7 ) * 1000 * 60 * 60 * 24)), new Date()];
            setCreateDateRange(rangeObj);
        }
        if(createDateOptionObj === 'lastWeek') {
            filtersObj.filter.createTimeStampFrom = new Date(new Date((new Date()).getTime() - ((((new Date()).getDay() + 6 ) % 7 ) * 1000 * 60 * 60 * 24)).getTime() -
            (86400000 * 7)).getTime();
            filtersObj.filter.createTimeStampTo = new Date((new Date()).getTime() - ((((new Date()).getDay() + 6 ) % 7 ) * 1000 * 60 * 60 * 24)).getTime();
            let rangeObj = [new Date(new Date((new Date()).getTime() - ((((new Date()).getDay() + 6 ) % 7 ) * 1000 * 60 * 60 * 24)).getTime() -
                (86400000 * 7)), new Date((new Date()).getTime() - ((((new Date()).getDay() + 6 ) % 7 ) * 1000 * 60 * 60 * 24))];
            setCreateDateRange(rangeObj);
        }
        if(createDateOptionObj === 'last7Days') {
            filtersObj.filter.createTimeStampFrom = (new Date()).getTime() - (86400000 * 7);
            filtersObj.filter.createTimeStampTo = (new Date()).getTime();
            let rangeObj = [new Date(new Date().getTime() - (86400000 * 7)), new Date()];
            setCreateDateRange(rangeObj);
        }
        if(createDateOptionObj === 'currentMonth') {
            filtersObj.filter.createTimeStampFrom = new Date((new Date()).getFullYear(), (new Date()).getMonth(), 1).getTime();
            filtersObj.filter.createTimeStampTo = (new Date()).getTime();
            let rangeObj = [new Date((new Date()).getFullYear(), (new Date()).getMonth(), 1), new Date()];
            setCreateDateRange(rangeObj);
        }
        if(createDateOptionObj === 'lastMonth') {
            filtersObj.filter.createTimeStampFrom = new Date((new Date()).getFullYear(), (new Date()).getMonth() - 1, 1).getTime();
            filtersObj.filter.createTimeStampTo = new Date((new Date()).getFullYear(), (new Date()).getMonth(), 0).getTime();
            let rangeObj = [new Date((new Date()).getFullYear(), (new Date()).getMonth() - 1, 1), new Date((new Date()).getFullYear(), (new Date()).getMonth(), 0)];
            setCreateDateRange(rangeObj);
        }
        props.onFilterChange(filtersObj);
        setFilters(filtersObj);
        setRefresh(!refresh);
    }

    const handleCreateDateChange = (ranges) => {
        let filtersObj = filters;
        filtersObj.filter.createTimeStampFrom = new Date(ranges[0]).getTime();
        filtersObj.filter.createTimeStampTo = new Date(ranges[1]).getTime();
        props.onFilterChange(filtersObj);
        setFilters(filtersObj);
        setCreateDateRange(ranges);
        setCreateDateOption('custom');
        setRefresh(!refresh);
    }

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        if(filters?.filter?.tableTypeList?.length > 0) count += filters.filter.tableTypeList.length;
        if(filters?.filter?.fileStoreTypeList?.length > 0) count += filters.filter.fileStoreTypeList.length;
        if(filters?.filter?.userIdList?.length > 0) count += filters.filter.userIdList.length;
        if(filters?.filter?.createTimeStampFrom > 0) count++;
        return count;
    };

    return (
        <div className={className}>
            <div className={`${className}__container`}>
                {/* Search Bar Section */}
                <div className={`${className}__searchBar`}>
                    <TextField
                        placeholder='Search files...'
                        value={searchQuery}
                        onChange={handleSearchQueryChange}
                        onKeyDown={handleSearchOnKeyPress}
                        size='small'
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon className={`${className}__searchIcon`} />
                                </InputAdornment>
                            ),
                        }}
                        className={`${className}__searchInput`}
                    />
                    <Button
                        variant='contained'
                        onClick={() => props.handleSearch(filters)}
                        className={`${className}__searchButton`}
                        startIcon={<SearchIcon />}
                    >
                        Search
                    </Button>
                </div>

                {/* Filters and Actions Row */}
                <div className={`${className}__filtersRow`}>
                    <div className={`${className}__filterChips`}>
                        {props.showTableTypeList && (
                            <Tooltip title="Filter by location type">
                                <Badge badgeContent={filters?.filter?.tableTypeList?.length || 0} color="primary">
                                    <Chip
                                        icon={<CategoryIcon />}
                                        label="Location"
                                        onClick={handleTypeOptionsPopup}
                                        variant={filters?.filter?.tableTypeList?.length > 0 ? "filled" : "outlined"}
                                        className={`${className}__filterChip`}
                                    />
                                </Badge>
                            </Tooltip>
                        )}

                        {props.showFileStoreTypeList && (
                            <Tooltip title="Filter by attachment type">
                                <Badge badgeContent={filters?.filter?.fileStoreTypeList?.length || 0} color="primary">
                                    <Chip
                                        icon={<AttachFileIcon />}
                                        label="Attachment"
                                        onClick={handleFileStoreTypeOptionsPopup}
                                        variant={filters?.filter?.fileStoreTypeList?.length > 0 ? "filled" : "outlined"}
                                        className={`${className}__filterChip`}
                                    />
                                </Badge>
                            </Tooltip>
                        )}

                        {props.showCreatedBy && (
                            <Tooltip title="Filter by creator">
                                <Badge badgeContent={filters?.filter?.userIdList?.length || 0} color="primary">
                                    <Chip
                                        icon={<PersonIcon />}
                                        label="Created By"
                                        onClick={handleCreatedByOptionPopup}
                                        variant={filters?.filter?.userIdList?.length > 0 ? "filled" : "outlined"}
                                        className={`${className}__filterChip`}
                                    />
                                </Badge>
                            </Tooltip>
                        )}

                        {props.showCreateDateFilter && (
                            <Tooltip title="Filter by date">
                                <Chip
                                    icon={<CalendarTodayIcon />}
                                    label="Date"
                                    onClick={handleCreateDateOptionsPopup}
                                    variant={filters?.filter?.createTimeStampFrom > 0 ? "filled" : "outlined"}
                                    className={`${className}__filterChip`}
                                />
                            </Tooltip>
                        )}

                        {props.showSortByFilter && (
                            <Tooltip title="Sort options">
                                <Chip
                                    icon={<SortIcon />}
                                    label={`Sort: ${sortByOption === 'createTimeStamp' ? 'Created' : 'Modified'}`}
                                    onClick={handleSortByOptionsPopup}
                                    variant="outlined"
                                    className={`${className}__filterChip`}
                                />
                            </Tooltip>
                        )}

                        {getActiveFiltersCount() > 0 && (
                            <Tooltip title="Clear all filters">
                                <Chip
                                    icon={<ClearIcon />}
                                    label="Clear All"
                                    onClick={clearFilters}
                                    color="error"
                                    variant="outlined"
                                    className={`${className}__clearChip`}
                                />
                            </Tooltip>
                        )}
                    </div>

                    <div className={`${className}__actions`}>
                        <Tooltip title="Download files">
                            <IconButton onClick={handleDownloadOptionsPopup} className={`${className}__downloadBtn`}>
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                {/* Location Type Menu */}
                {showTypeOptions && (
                    <Menu
                        open={showTypeOptions}
                        onClose={handleTypeOptionsPopup}
                        anchorEl={anchorTypeOptionsMenu}
                        className={`${className}__menu`}
                        PaperProps={{
                            className: `${className}__menuPaper`
                        }}
                    >
                        <div className={`${className}__menuHeader`}>
                            <CategoryIcon />
                            <span>Location Type</span>
                        </div>
                        {['EPIC', 'STORY', 'STAGE', 'MILESTONE', 'TASK', 'TICKET'].map((type) => (
                            <MenuItem key={type} className={`${className}__menuItem`}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters?.filter?.tableTypeList?.some((item) => item === type)}
                                            onChange={() => toggleTypeCheckbox(type)}
                                        />
                                    }
                                    label={type.charAt(0) + type.slice(1).toLowerCase()}
                                />
                            </MenuItem>
                        ))}
                    </Menu>
                )}

                {/* File Store Type Menu */}
                {showFileStoryTypeOptions && (
                    <Menu
                        open={showFileStoryTypeOptions}
                        onClose={handleFileStoreTypeOptionsPopup}
                        anchorEl={anchorFileStoreTypeOptionsMenu}
                        className={`${className}__menu`}
                        PaperProps={{
                            className: `${className}__menuPaper`
                        }}
                    >
                        <div className={`${className}__menuHeader`}>
                            <AttachFileIcon />
                            <span>Attachment Type</span>
                        </div>
                        <MenuItem className={`${className}__menuItem`}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters?.filter?.fileStoreTypeList?.some((fileType) => fileType === 'comment')}
                                        onChange={() => toggleFileStoreType('comment')}
                                    />
                                }
                                label="Comment"
                            />
                        </MenuItem>
                        <MenuItem className={`${className}__menuItem`}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters?.filter?.fileStoreTypeList?.some((fileType) => fileType === 'attachment')}
                                        onChange={() => toggleFileStoreType('attachment')}
                                    />
                                }
                                label="Attachment"
                            />
                        </MenuItem>
                    </Menu>
                )}

                {/* Created By Menu */}
                {showCreatedByListOption && (
                    <Menu
                        open={showCreatedByListOption}
                        onClose={handleCreatedByOptionPopup}
                        anchorEl={anchorCreatedByListOptionMenu}
                        className={`${className}__menu`}
                        PaperProps={{
                            className: `${className}__menuPaper ${className}__menuPaper--scrollable`
                        }}
                    >
                        <div className={`${className}__menuHeader`}>
                            <PersonIcon />
                            <span>Created By</span>
                        </div>
                        {props.userList?.map((user) => (
                            <MenuItem key={user.id} className={`${className}__menuItem`}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters?.filter?.userIdList?.some((assigneeId) => assigneeId === user.id)}
                                            onChange={() => toggleCreatedByCheckBox(user.id)}
                                        />
                                    }
                                    label={user.fullName}
                                />
                            </MenuItem>
                        ))}
                    </Menu>
                )}

                {/* Sort By Menu */}
                {showSortByOptions && (
                    <Menu
                        open={showSortByOptions}
                        onClose={handleSortByOptionsPopup}
                        anchorEl={anchorSortByOptionsMenu}
                        className={`${className}__menu`}
                        PaperProps={{
                            className: `${className}__menuPaper`
                        }}
                    >
                        <div className={`${className}__menuHeader`}>
                            <SortIcon />
                            <span>Sort By</span>
                        </div>
                        <div className={`${className}__sortContent`}>
                            <FormControl className={`${className}__formControl`}>
                                <RadioGroup
                                    value={sortByOption}
                                    onChange={handleSortByChange}
                                >
                                    <FormControlLabel value='createTimeStamp' control={<Radio />} label='Created Date' />
                                    <FormControlLabel value='modifiedTimeStamp' control={<Radio />} label='Last Modified' />
                                </RadioGroup>
                                <div className={`${className}__switchRow`}>
                                    <span>Ascending Order</span>
                                    <Switch
                                        onChange={toggleSortAsc}
                                        checked={filters?.filter?.sortAsc}
                                        onColor='#1976d2'
                                        onHandleColor='#1565c0'
                                        handleDiameter={20}
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        height={14}
                                        width={32}
                                    />
                                </div>
                            </FormControl>
                        </div>
                    </Menu>
                )}

                {/* Create Date Menu */}
                {showCreateDateOptions && (
                    <Menu
                        open={showCreateDateOptions}
                        onClose={handleCreateDateOptionsPopup}
                        anchorEl={anchorCreateDateOptionsMenu}
                        className={`${className}__menu`}
                        PaperProps={{
                            className: `${className}__menuPaper ${className}__menuPaper--wide`
                        }}
                    >
                        <div className={`${className}__menuHeader`}>
                            <CalendarTodayIcon />
                            <span>Filter by Date</span>
                        </div>
                        <div className={`${className}__dateContent`}>
                            <FormControl className={`${className}__formControl`}>
                                <RadioGroup
                                    value={createDateOption}
                                    onChange={handleCreateDateOptionChange}
                                >
                                    <FormControlLabel value='custom' control={<Radio />} label='Custom' />
                                    <FormControlLabel value='thisWeek' control={<Radio />} label='This Week' />
                                    <FormControlLabel value='lastWeek' control={<Radio />} label='Last Week' />
                                    <FormControlLabel value='last7Days' control={<Radio />} label='Last 7 Days' />
                                    <FormControlLabel value='currentMonth' control={<Radio />} label='Current Month' />
                                    <FormControlLabel value='lastMonth' control={<Radio />} label='Last Month' />
                                </RadioGroup>
                            </FormControl>
                            <div className={`${className}__calendar`}>
                                <Calendar
                                    value={createDateRange}
                                    onChange={(e) => handleCreateDateChange(e.value)}
                                    selectionMode='range'
                                    inline
                                />
                            </div>
                        </div>
                    </Menu>
                )}

                {/* Download Options Menu */}
                {showDownloadOptions && (
                    <Menu
                        open={showDownloadOptions}
                        onClose={handleDownloadOptionsPopup}
                        anchorEl={anchorDownloadOptionsMenu}
                        className={`${className}__menu`}
                        PaperProps={{
                            className: `${className}__menuPaper ${className}__menuPaper--wide`
                        }}
                    >
                        <div className={`${className}__menuHeader`}>
                            <DownloadIcon />
                            <span>Download Options</span>
                        </div>
                        <div className={`${className}__downloadContent`}>
                            <FormControl className={`${className}__formControl`}>
                                <RadioGroup
                                    value={createDateOption}
                                    onChange={handleCreateDateOptionChange}
                                >
                                    <FormControlLabel value='custom' control={<Radio />} label='Custom Date Range' />
                                    <FormControlLabel value='thisWeek' control={<Radio />} label='This Week' />
                                    <FormControlLabel value='lastWeek' control={<Radio />} label='Last Week' />
                                    <FormControlLabel value='last7Days' control={<Radio />} label='Last 7 Days' />
                                    <FormControlLabel value='currentMonth' control={<Radio />} label='Current Month' />
                                    <FormControlLabel value='lastMonth' control={<Radio />} label='Last Month' />
                                </RadioGroup>
                                <div className={`${className}__switchRow`}>
                                    <span>Maintain Folder Structure</span>
                                    <Switch
                                        onChange={toggleMaintainFolder}
                                        checked={maintainFolder}
                                        onColor='#1976d2'
                                        onHandleColor='#1565c0'
                                        handleDiameter={20}
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        height={14}
                                        width={32}
                                    />
                                </div>
                            </FormControl>
                            <div className={`${className}__calendar`}>
                                <Calendar
                                    value={createDateRange}
                                    onChange={(e) => handleCreateDateChange(e.value)}
                                    selectionMode='range'
                                    inline
                                />
                            </div>
                        </div>
                        <div className={`${className}__menuFooter`}>
                            <Button
                                variant='contained'
                                startIcon={<DownloadIcon />}
                                onClick={handleDownload}
                                fullWidth
                            >
                                Download Files
                            </Button>
                        </div>
                    </Menu>
                )}
            </div>

            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                open={snackBarOpen}
                autoHideDuration={5000}
                onClose={handleSnackBarClose}
            >
                <Alert
                    onClose={handleSnackBarClose}
                    severity={snackBarType}
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    {snackBarMessage}
                </Alert>
            </Snackbar>
        </div>
    )
}

FileFilter.defaultProps = {
    showTableTypeList: true,
    showFileStoreTypeList: true,
    showSortByFilter: true,
    showCreateDateFilter: true,
    showCreatedBy: true,
}